import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Linkedin, Github, Mail, Send } from 'lucide-react';
import { useColorMode } from '../../contexts/ColorModeContext';

const CONTACT_EMAIL = 'contact@consultsre.com';

const Footer: React.FC = () => {
  const { mode } = useColorMode();
  const [email, setEmail] = useState('');
  const [state, setState] = useState<'idle' | 'submitting' | 'ok' | 'err'>('idle');
  const [errMsg, setErrMsg] = useState('');

  const subscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setState('submitting');
    setErrMsg('');
    try {
      const res = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Subscription failed');
      setState('ok');
      setEmail('');
    } catch (err) {
      setState('err');
      setErrMsg(err instanceof Error ? err.message : 'Subscription failed');
    }
  };

  return (
    <footer className="footer">
      <div className="footer__inner">
        <div className="footer__brand">
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 12 }}>
            <img
              className="navbar__logo-mark"
              src={mode === 'light' ? '/brand/consultsre-mark-light.svg' : '/brand/consultsre-mark-dark.svg'}
              alt=""
              aria-hidden="true"
              width={28}
              height={28}
            />
            <span
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 18,
                fontWeight: 700,
                letterSpacing: '-0.02em',
                color: 'var(--ink)',
              }}
            >
              Consult<span style={{ color: 'var(--accent)' }}>SRE</span>
              <span className="brand-caret" aria-hidden="true" />
            </span>
          </div>
          <p>
            Reliability is a product feature. We ship it.
          </p>
          <div
            style={{
              marginTop: 16,
              display: 'flex',
              gap: 8,
            }}
          >
            <a
              href="https://www.linkedin.com/company/consultsre"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="ConsultSRE on LinkedIn"
              className="footer__social"
            >
              <Linkedin size={14} />
            </a>
            <a
              href="https://github.com/ConsultSRE"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="ConsultSRE on GitHub"
              className="footer__social"
            >
              <Github size={14} />
            </a>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              aria-label={`Email ${CONTACT_EMAIL}`}
              className="footer__social"
            >
              <Mail size={14} />
            </a>
          </div>
          <div
            style={{
              marginTop: 14,
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
              color: 'var(--ink-mute)',
            }}
          >
            <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: 'var(--accent)', textDecoration: 'none' }}>
              {CONTACT_EMAIL}
            </a>
            <span style={{ marginLeft: 10 }}>· remote-first · global</span>
          </div>
        </div>

        <div>
          <h5>Navigate</h5>
          <ul>
            <li><Link to="/services">Services</Link></li>
            <li><Link to="/about-us">About</Link></li>
            <li><Link to="/blog">Blog</Link></li>
<li><Link to="/contact">Contact</Link></li>
          </ul>
        </div>

        <div>
          <h5>Services</h5>
          <ul>
            <li><Link to="/services/sre">SRE Implementation</Link></li>
            <li><Link to="/services/observability">Observability</Link></li>
            <li><Link to="/services/kubernetes">Kubernetes</Link></li>
            <li><Link to="/services/cloud">Cloud Architecture</Link></li>
            <li><Link to="/services/cicd">CI/CD Excellence</Link></li>
            <li><Link to="/services/incident">Incident Response</Link></li>
            <li><Link to="/services/security">Security</Link></li>
            <li><Link to="/services">All services →</Link></li>
          </ul>
        </div>

        <div className="footer__subscribe">
          <h5>Newsletter</h5>
          <p
            style={{
              fontSize: 13,
              color: 'var(--ink-dim)',
              lineHeight: 1.5,
              marginBottom: 12,
            }}
          >
            Monthly SRE insights. No fluff.
          </p>
          {state === 'ok' ? (
            <div
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 11,
                color: 'var(--green)',
              }}
            >
              ◆ check your inbox to confirm
            </div>
          ) : (
            <form onSubmit={subscribe} style={{ display: 'flex', gap: 6 }}>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                aria-label="Email address"
                style={{
                  flex: 1,
                  minWidth: 0,
                  padding: '8px 10px',
                  fontSize: 12,
                  fontFamily: 'var(--font-body)',
                  background: 'var(--bg-elev)',
                  border: '1px solid var(--line)',
                  borderRadius: 'var(--r-xs)',
                  color: 'var(--ink)',
                  outline: 'none',
                }}
              />
              <button
                type="submit"
                disabled={state === 'submitting'}
                className="footer__subscribe-btn"
                aria-label="Subscribe"
              >
                <Send size={12} />
              </button>
            </form>
          )}
          {state === 'err' && (
            <div
              style={{
                marginTop: 6,
                fontFamily: 'var(--font-mono)',
                fontSize: 10.5,
                color: 'var(--rose)',
              }}
            >
              ✕ {errMsg}
            </div>
          )}
        </div>
      </div>

      <div className="footer__bottom">
        <span>© {new Date().getFullYear()} ConsultSRE</span>
        <span className="footer__legal">
          <Link to="/privacy-policy">Privacy</Link>
          <span aria-hidden="true">·</span>
          <Link to="/terms">Terms</Link>
        </span>
      </div>
    </footer>
  );
};

export default Footer;
